import { createFileRoute, redirect } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import { formatTimestamp } from "../../../libs/date";
import { AppError, ERROR_CODES } from "../../../libs/errors";
import { getSortedBalancesWithFundSources } from "../../../libs/get-sorted-balances-with-fund-sources";
import { t } from "../../../libs/i18n";
import { formatNumber } from "../../../libs/utils";

export const Route = createFileRoute("/_authed/transfer/")({
  beforeLoad: ({ context: { tenant } }) => {
    if (!tenant.feature.transfer) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context: { client } }) => {
    const balanceResponse = await client.getMyBalances({}).catch((error) => {
      throw new AppError(
        ERROR_CODES.CONNECT_GET_MY_BALANCES_FAILED,
        error.message,
        {
          originalError: error,
        },
      );
    });

    const { sortedBalances } = await getSortedBalancesWithFundSources(
      client,
      balanceResponse.balanceWithFundSources,
    );

    const balances = sortedBalances.filter(
      (b) => b?.userBalance?.amount && b.userBalance.amount > 0,
    );

    return {
      balances,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { balances } = Route.useLoaderData();
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  const navigate = useNavigate();

  const handlePointSelect = (id: string, isDisabled: boolean) => {
    if (isDisabled) {
      return;
    }
    setSelectedPointId(id);
  };

  const handleSubmit = () => {
    if (selectedPointId) {
      navigate({
        to: "/transfer/input/$fundSourceId/$amount",
        params: {
          fundSourceId: selectedPointId,
          amount: "0",
        },
        search: { from: "transfer" },
      });
    }
  };

  const handleKeyDown = (
    id: string,
    e: React.KeyboardEvent,
    isDisabled: boolean,
  ) => {
    if (isDisabled) {
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      handlePointSelect(id, isDisabled);
    }
  };

  return (
    <>
      <Header
        title={t("ポイントを送る")}
        onClickBack={() => navigate({ to: "/" })}
      />
      <div
        className={css({
          padding: "16px",
          pb: "64px",
          display: "flex",
          flexDir: "column",
          gap: "16px",
        })}
      >
        <div
          className={css({ display: "flex", flexDir: "column", gap: "8px" })}
        >
          <h1
            className={css({
              color: "text.primary",
            })}
          >
            {t("送るポイントの選択")}
          </h1>
          <div
            className={css({
              color: "text.secondary",
              fontSize: "14px",
            })}
          >
            {t("送るポイントの種類を選択してください")}
          </div>
        </div>
        {balances.length === 0 && (
          <div
            className={css({
              fontSize: "16px",
              textAlign: "center",
              fontWeight: "bold",
              color: "text.secondary",
              mb: "32px",
              mt: "42px",
            })}
          >
            {t("利用可能なポイントがありません。")}
          </div>
        )}
        <div
          className={css({
            display: "flex",
            flexDir: "column",
            gap: "12px",
          })}
        >
          {balances.map((balance) => {
            const fundSourceId = balance.fundSource?.id ?? "";
            const isSelected = selectedPointId === fundSourceId;
            const isDisabled = Boolean(balance.fundSource?.forbidTransfer);

            return (
              <div
                key={fundSourceId}
                className={css({
                  display: "flex",
                  alignItems: "center",
                  py: isDisabled ? "12px" : "16px",
                  px: "20px",
                  border: "1px solid",
                  borderColor: isDisabled
                    ? "rgba(156, 163, 175, 0.5)"
                    : isSelected
                      ? "surface.accentPrimary"
                      : "border.primary",
                  borderRadius: isDisabled ? "12px" : "16px",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  backgroundColor: isDisabled ? "#e4e4e7" : "white",
                  gap: "12px",
                })}
                onClick={() => handlePointSelect(fundSourceId, isDisabled)}
                onKeyDown={(e) => handleKeyDown(fundSourceId, e, isDisabled)}
                aria-checked={isSelected}
                aria-disabled={isDisabled}
                // biome-ignore lint/a11y/useSemanticElements: <explanation>
                role="radio"
                tabIndex={isDisabled ? -1 : 0}
              >
                <label
                  htmlFor={fundSourceId}
                  className={css({
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    color: isDisabled ? "#9ca3af" : "text.primary",
                    gap: "4px",
                  })}
                >
                  <span
                    className={css({ fontWeight: "400", fontSize: "16px" })}
                  >
                    {balance.fundSource?.name}
                  </span>
                  <span
                    className={css({
                      fontSize: "14px",
                      color: isDisabled ? "#9ca3af" : "text.tertiary",
                    })}
                  >
                    {t("残高: {amount} ポイント", {
                      amount: formatNumber(balance.userBalance?.amount),
                    })}
                  </span>
                  {balance.userBalance?.expireAt && (
                    <span
                      className={css({
                        fontSize: "14px",
                        color: isDisabled ? "#9ca3af" : "text.tertiary",
                      })}
                    >
                      {balance.expirationDates.length > 1 ? (
                        <>
                          {t("最短有効期限:")}{" "}
                          {formatTimestamp(balance.userBalance?.expireAt)}
                        </>
                      ) : (
                        <>
                          {t("有効期限:")}{" "}
                          {formatTimestamp(balance.userBalance?.expireAt)}
                        </>
                      )}
                    </span>
                  )}
                  {isDisabled && (
                    <span
                      className={css({
                        fontSize: "14px",
                        color: "#4b556380",
                        mt: "4px",
                      })}
                    >
                      {t("このポイントは譲渡できません")}
                    </span>
                  )}
                </label>
                <input
                  type="radio"
                  id={fundSourceId}
                  name="point"
                  disabled={isDisabled}
                  checked={isSelected}
                  onChange={() => handlePointSelect(fundSourceId, isDisabled)}
                  className={css({
                    marginLeft: "12px",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    accentColor: isDisabled ? "#9ca3af80" : "#001efb",
                  })}
                />
              </div>
            );
          })}
        </div>
        <button
          onClick={handleSubmit}
          disabled={!selectedPointId}
          type="button"
          className={css({
            width: "100%",
            padding: "12px",
            backgroundColor: selectedPointId
              ? "surface.accentPrimary"
              : "#e0e0e0",
            color: selectedPointId ? "white" : "#666",
            borderRadius: "16px",
            fontWeight: "bold",
            cursor: selectedPointId ? "pointer" : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          })}
        >
          <div>{t("次へ")}</div>
          <ArrowForward
            className={css({
              width: "24px",
              height: "24px",
            })}
          />
        </button>
      </div>
    </>
  );
}
